// src/components/product/RelatedProduct.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { getRelatedProducts } from '../../services/products';
import '../../styles/RelatedProduct.css';

const ITEMS_PER_PAGE = 4;

const normUrl = (u) => (typeof u === 'string' ? u.replace('http://', 'https://') : u);

const resolveCatId = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw.id ?? null;
  return raw;
};

// ── Resolve final price from new response shape ──
const resolvePrice = (p) => {
  const final    = Number(p?.final_price            ?? 0);
  const flash    = Number(p?.flash_sale_price       ?? 0);
  const discount = Number(p?.product_discount_price ?? 0);
  const regular  = Number(p?.price                  ?? 0);
  if (final > 0)    return final;
  if (flash > 0)    return flash;
  if (discount > 0) return discount;
  return regular;
};

const toProduct = (p) => {
  const firstImg =
    Array.isArray(p.product_images) && p.product_images.length
      ? normUrl(p.product_images[0])
      : normUrl(p.image_url || p.thumbnail || '');

  const price         = resolvePrice(p);
  const originalPrice = Number(p.price ?? 0);
  const isFlashSale   = !!p.is_flash_sale;
  const hasDiscount   = originalPrice > price && originalPrice > 0;
  const discountPct   = hasDiscount
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : 0;

  return {
    id:           p.id,
    slug:         p.slug ?? String(p.id),
    title:        p.name || p.title || 'Product',
    price,
    originalPrice,
    isFlashSale,
    hasDiscount,
    discountPct,
    rating:       Number(p.average_rating ?? 0),
    reviews:      Number(p.total_reviews  ?? 0),
    image:        firstImg,
    brand:        p.brand || '',
    categoryId:   p.category?.id ?? p.category_id ?? null,
  };
};

/* ── Stars ── */
const Stars = ({ rating }) => {
  const full    = Math.floor(rating || 0);
  const hasHalf = (rating || 0) % 1 >= 0.5;
  const empty   = 5 - full - (hasHalf ? 1 : 0);
  return (
    <div className="rp-stars">
      {Array.from({ length: full }).map((_, i) => (
        <svg key={`f${i}`} className="rp-star rp-star--full" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
        </svg>
      ))}
      {hasHalf && (
        <svg key="h" className="rp-star" width="12" height="12" viewBox="0 0 24 24">
          <defs><linearGradient id="rpHalf"><stop offset="50%" stopColor="#f77321"/><stop offset="50%" stopColor="#e4e8f0"/></linearGradient></defs>
          <path fill="url(#rpHalf)" d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
        </svg>
      )}
      {Array.from({ length: empty }).map((_, i) => (
        <svg key={`e${i}`} className="rp-star rp-star--empty" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
        </svg>
      ))}
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   RelatedProduct
════════════════════════════════════════════════════════ */
const RelatedProduct = ({ categoryId, excludeId, onNavigate }) => {
  const [page,    setPage]    = useState(0);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(false);

  const maxPage = useMemo(
    () => Math.max(0, Math.ceil(related.length / ITEMS_PER_PAGE) - 1),
    [related.length]
  );

  useEffect(() => {
    const catId = resolveCatId(categoryId);
    if (!catId) return;
    setPage(0);

    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await getRelatedProducts({
          categoryId: catId,
          excludeId,
          limit: 100,
        });

        const raw = Array.isArray(data?.data) ? data.data
                  : Array.isArray(data)        ? data : [];

        if (mounted) {
          setRelated(
            raw
              .map(toProduct)
              .filter((p) => String(p.categoryId) === String(catId))
              .filter((p) => String(p.id)         !== String(excludeId))
          );
        }
      } catch (err) {
        console.error('[RELATED] fetch failed:', err);
        if (mounted) setRelated([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [categoryId, excludeId]);

  const handlePrev = () => setPage((p) => (p > 0 ? p - 1 : maxPage));
  const handleNext = () => setPage((p) => (p < maxPage ? p + 1 : 0));
  const canPage    = related.length > ITEMS_PER_PAGE;
  const visible    = related.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  const handleNavigate = (product) => { if (onNavigate) onNavigate(product.slug); };

  return (
    <div className="rp-root">
      {/* Header */}
      <div className="rp-header">
        <div className="rp-header-left">
          <h2 className="rp-title">Related Items</h2>
          {!loading && related.length > 0 && (
            <span className="rp-count">{related.length} items</span>
          )}
        </div>
        <div className="rp-arrows">
          <button className="rp-arrow" onClick={handlePrev} disabled={!canPage} aria-label="Previous">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="rp-arrow" onClick={handleNext} disabled={!canPage} aria-label="Next">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Dots */}
      {canPage && (
        <div className="rp-dots">
          {Array.from({ length: maxPage + 1 }).map((_, i) => (
            <button key={i} className={`rp-dot${i === page ? ' active' : ''}`} onClick={() => setPage(i)} aria-label={`Page ${i + 1}`}/>
          ))}
        </div>
      )}

      {/* States */}
      {loading && (
        <div className="rp-state"><span className="rp-spinner"/>Loading related items…</div>
      )}
      {!loading && related.length === 0 && (
        <div className="rp-state rp-state--empty">No related items found</div>
      )}

      {/* Grid */}
      {!loading && visible.length > 0 && (
        <div className="rp-grid">
          {visible.map((product) => (
            <div key={product.id} className="rp-card" onClick={() => handleNavigate(product)}>
              <div className="rp-img-wrap">
                <img
                  src={product.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23f2f4f8" width="300" height="300"/%3E%3Ctext x="150" y="165" text-anchor="middle" font-size="52"%3E%F0%9F%93%A6%3C/text%3E%3C/svg%3E'}
                  alt={product.title}
                  className="rp-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23f2f4f8" width="300" height="300"/%3E%3Ctext x="150" y="165" text-anchor="middle" font-size="52"%3E%F0%9F%93%A6%3C/text%3E%3C/svg%3E';
                  }}
                />
                {/* Flash badge */}
                {product.isFlashSale && (
                  <span className="rp-badge rp-badge-flash">⚡ Flash</span>
                )}
                {/* Regular discount badge */}
                {!product.isFlashSale && product.hasDiscount && product.discountPct > 0 && (
                  <span className="rp-badge">-{product.discountPct}%</span>
                )}
              </div>

              <div className="rp-info">
                {product.brand && <span className="rp-brand">{product.brand}</span>}
                <h3 className="rp-product-title">{product.title}</h3>

                {product.rating > 0 && (
                  <div className="rp-rating">
                    <Stars rating={product.rating}/>
                    <span className="rp-rating-text">
                      {product.rating.toFixed(1)}
                      {product.reviews > 0 && <span className="rp-review-count"> ({product.reviews})</span>}
                    </span>
                  </div>
                )}

                <div className="rp-price-row">
                  <div className="rp-prices">
                    <span className={`rp-price${product.isFlashSale ? ' rp-price-flash' : ''}`}>
                      ${product.price.toFixed(2)}
                    </span>
                    {product.hasDiscount && (
                      <span className="rp-orig-price">${product.originalPrice.toFixed(2)}</span>
                    )}
                  </div>
                  <button
                    className="rp-nav-btn"
                    onClick={(e) => { e.stopPropagation(); handleNavigate(product); }}
                    aria-label="View product"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RelatedProduct;